/**
 * Cart Payment Methods Controller
 * Manages payment method visibility based on cart purchase type
 */

// Global state management
let cartPaymentOptionWrapper = null;
let cartDataForPaymentSelectionInCart = null;
let cartAvailablePaymentMethods = {
  one_time: [],
  subscription: [],
  mixed: [],
};
let cartSelectedPaymentMethod = null;
let cartPaymentSettingsLoaded = false;

function ensurePaymentSettings() {
  if (cartPaymentSettingsLoaded) return;
  try {
    cartAvailablePaymentMethodsFromSettings();
    cartPaymentSettingsLoaded = true;
  } catch (error) {
    console.error('cartAvailablePaymentMethodsFromSettings failed', error);
  }
}

function availableMethodsFor(purchaseType) {
  ensurePaymentSettings();
  const key = purchaseType === "onetime" ? "one_time" : purchaseType;
  return cartAvailablePaymentMethods[key] || [];
}

function resolveCartPaymentMethod(purchaseType, preferredMethod) {
  const allowed = availableMethodsFor(purchaseType);
  const preferred = String(preferredMethod || "").trim();
  if (allowed.length === 0 || allowed.includes(preferred)) return preferred;
  return allowed.includes("Online") ? "Online" : allowed[0];
}

/**
 * Fetch wrapper that retries transient failures (network errors and 5xx
 * responses) with exponential backoff. Used for /cart/update.js, which
 * intermittently returns 503 / "Load failed" on some markets and otherwise
 * leaves the cart overlay stuck. 4xx is NOT retried — it signals bad request
 * data, not a transient fault.
 */
const fetchCartUpdateWithRetry = async (url, options, retries = 2, baseDelay = 400) => {
  for (let attempt = 0; ; attempt++) {
    try {
      // Route through the cart service so the write is serialized against other
      // cart mutations (queue) and timed out; the retry loop here still handles
      // transient 5xx. Falls back to a direct fetch if the service isn't present.
      const response = (window.JPCartService && typeof window.JPCartService.enqueuePost === 'function')
        ? await window.JPCartService.enqueuePost(url, options && options.body, options && options.headers)
        : await fetch(url, options);
      if (response.status >= 500 && attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
        continue;
      }
      return response;
    } catch (error) {
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, baseDelay * Math.pow(2, attempt)));
        continue;
      }
      throw error;
    }
  }
};

/**
 * Load current cart data
 */
async function loadcartDataForPaymentSelectionInCart(force) {
  try {
    // Initial load uses the shared (deduped, warmed) cart; refreshes after a
    // mutation pass force to bypass the cache and re-read the updated cart.
    cartDataForPaymentSelectionInCart = window.JPCartService
      ? await window.JPCartService.getCart(force ? { force: true } : undefined)
      : await (await fetch("/cart.js")).json();

    // Always update the global reference
    window.cartDataForPaymentSelectionInCart = cartDataForPaymentSelectionInCart;
  } catch (error) {
    console.error('🛒 Error loading cart data:', error);
    cartDataForPaymentSelectionInCart = { items: [], attributes: {} };
  }
}

/**
 * Get current cart purchase type (mini cart uses internal cart data; section can pass items)
 * @param {Array} [cartItemsOverride] - Optional items array (e.g. from checkout section); else uses cartDataForPaymentSelectionInCart
 * @returns {'onetime'|'subscription'|'mixed'}
 */
function getCartPurchaseType(cartItemsOverride) {
  return analyzeCartItems(cartItemsOverride);
}

/**
 * Analyze cart items to determine purchase type.
 * Same logic for mini cart and checkout section; accepts optional items so section can pass cart items.
 * @param {Array} [cartItemsOverride] - Optional items array; if omitted, uses cartDataForPaymentSelectionInCart.items
 * @returns {'onetime'|'subscription'|'mixed'}
 */
function analyzeCartItems(cartItemsOverride) {
  const cartItems = cartItemsOverride ?? cartDataForPaymentSelectionInCart?.items;

  if (!cartItems || cartItems.length === 0) {
    return 'onetime';
  }
  
  const subscriptionCount = cartItems.filter((item) => {
    // Check if any of the three subscription criteria are satisfied
    return (
      // 1. Check properties._subscriptionType
      (item.properties &&
        item.properties._subscriptionType &&
        item.properties._subscriptionType !== "") ||
      // 2. Check properties._sellingplan
      (item.properties &&
        item.properties._sellingplan &&
        item.properties._sellingplan !== "") ||
      // 3. Check selling_plan_allocation
      (item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan)
    );
  }).length;
  const onetimeCount = cartItems.length - subscriptionCount;
  
  if (subscriptionCount > 0 && onetimeCount === 0) {
    return 'subscription';
  } else if (subscriptionCount === 0 && onetimeCount > 0) {
    return 'onetime';
  } else if (subscriptionCount > 0 && onetimeCount > 0) {
    return 'mixed';
  }
  
  return 'onetime'; // Default fallback
}

/**
 * Update cart _newFlow attribute
 *
 * Idempotency guard: cartDataForPaymentSelectionInCart already holds the most
 * recent cart state (refreshed by load + every successful update here). If the
 * target attributes already match, skip the POST — this is the common case
 * when initCartPaymentMethods runs on a page where the user hasn't changed
 * anything since their last visit.
 */
async function updateCartAttribute(newFlowValue, availablePayments) {
  try {
    if (typeof window.awaitSharedCartRestore === 'function') {
      await window.awaitSharedCartRestore();
    }

    const currentAttrs = cartDataForPaymentSelectionInCart?.attributes || {};
    const currentNewFlow = currentAttrs._newFlow;
    const currentAvailablePayments = currentAttrs._availablePayments;

    const newFlowMatches = String(currentNewFlow ?? '') === String(newFlowValue ?? '');
    const availableMatches = !availablePayments
      || JSON.stringify(currentAvailablePayments ?? null) === JSON.stringify(availablePayments);

    if (newFlowMatches && availableMatches) {
      return;
    }

    const response = await fetchCartUpdateWithRetry("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // Empty `updates`: see withUpdatesField in cart-service.js.
      body: JSON.stringify({
        attributes: {
          _newFlow: newFlowValue,
          ...(availablePayments ? {_availablePayments: availablePayments} : {}),
        },
        updates: {},
      }),
    });

    if (response.ok) {
      const updatedCart = await response.json();
      cartDataForPaymentSelectionInCart = updatedCart;
    } else {
      console.error(
        "Failed to update cart _newFlow attribute:",
        response.statusText
      );
    }
  } catch (error) {
    console.error("Error updating cart _newFlow attribute:", error);
  }
}

/**
 * Update cart _paymentMethod attribute and manage selling plan allocations
 */
async function updateCartPaymentMethodAttribute(allowedPaymentMethodValue) {
  if (typeof window.awaitSharedCartRestore === 'function') {
    await window.awaitSharedCartRestore();
  }

  const selectedMethod = cartPaymentOptionWrapper?.querySelector('input[name="payment-method"]:checked');
  
  if (!selectedMethod && !allowedPaymentMethodValue) {
    console.log('🛒 No payment method selected');
    return;
  }
  
  const paymentMethodValue = selectedMethod ? selectedMethod.value : allowedPaymentMethodValue;

  // Idempotency: skip both the POST and the downstream selling-plan reshuffle
  // when the cart already has this payment method, judged by the cart attribute
  // (current after loadcartDataForPaymentSelectionInCart).
  const existingPaymentMethod = cartDataForPaymentSelectionInCart?.attributes?._paymentMethod;
  if (existingPaymentMethod === paymentMethodValue) {
    cartSelectedPaymentMethod = paymentMethodValue;

    // ECOM-932: the attribute is already correct, so skipping the POST is right -- but the
    // cart ITEMS may have changed since it was set, and returning here skipped their
    // reconciliation too. The Become Partner journey hits this exactly: the Partner
    // Application Fee writes _paymentMethod when it is added, the customer then returns
    // from checkout and adds a product, and that new line never gets its selling plan
    // allocation reconciled for the already-chosen method. Checkout then finds no usable
    // payment method and reports "This store can't accept payments right now".
    //
    // handleSellingPlanAllocations is per-item idempotent (it only strips an allocation
    // that exists for manual methods, or adds one that is missing for card methods), so
    // running it here is a no-op when the cart is already consistent.
    await handleSellingPlanAllocations(paymentMethodValue);
    return;
  }

  try {
    // First, update the payment method attribute
    const response = await fetchCartUpdateWithRetry('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          _paymentMethod: paymentMethodValue
        },
        updates: {}
      })
    });
    
    if (response.ok) {
      const updatedCart = await response.json();
      
      // Update local cart data
      cartDataForPaymentSelectionInCart = updatedCart;
      cartSelectedPaymentMethod = paymentMethodValue;
      
      // Now handle selling plan allocations based on payment method
      await handleSellingPlanAllocations(paymentMethodValue);

      // Refresh cart display after payment method change
      if (typeof window.refreshCartPaymentChange === "function") {
        await window.refreshCartPaymentChange();
      }
      
    } else {
      console.error('Failed to update cart _paymentMethod attribute:', response.statusText);
    }
  } catch (error) {
    console.error('Error updating cart _paymentMethod attribute:', error);
  }
}

/**
 * Handle selling plan allocations based on payment method
 */
async function handleSellingPlanAllocations(paymentMethodValue) {
  if (!cartDataForPaymentSelectionInCart || !cartDataForPaymentSelectionInCart.items) {
    console.log('🛒 No cart data available for selling plan management');
    return;
  }
    
  // Get subscription products based on _subscriptionType property
  const subscriptionItems = cartDataForPaymentSelectionInCart.items.filter(item =>{
    // Check if any of the three subscription criteria are satisfied
    return (
      // 1. Check properties._subscriptionType
      (item.properties &&
        item.properties._subscriptionType &&
        item.properties._subscriptionType !== "") ||
      // 2. Check properties._sellingplan
      (item.properties &&
        item.properties._sellingplan &&
        item.properties._sellingplan !== "") ||
      // 3. Check selling_plan_allocation
      (item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan)
    );
  });
  
  if (subscriptionItems.length === 0) {
    console.log('🛒 No subscription items found in cart');
    return;
  }
  
  const isManualPayment = KROWN.settings.manualPaymentTypes.includes(paymentMethodValue);
  
  if (isManualPayment) {
    // Non-card payment: Remove selling plan allocations but store the ID first

    for (const item of subscriptionItems) {
      if (item.selling_plan_allocation) {

        // Store the selling plan ID in properties and remove selling plan allocation.
        // Same properties as a manual-payment add (cartLinePayload in src/model/cart/api.ts),
        // so a later add of the same product merges into this line instead of splitting.
        const sellingPlanId = `${item.selling_plan_allocation.selling_plan.id}`;
        await updateCartItem(item.key, {
          properties: { _sellingplan: sellingPlanId, _sub_engine_selling_plan: sellingPlanId },
          sellingPlanId: null,
        });

      }
    }
  } else {
    // Card payment: Ensure subscription products have selling plan allocation
    for (const item of subscriptionItems) {
      if (!item.selling_plan_allocation) {

        // Use stored selling plan ID from properties if available, otherwise get first available
        let sellingPlanId = null;
        if (item.properties && item.properties._sellingplan) {
          sellingPlanId = item.properties._sellingplan;
        }

        if (sellingPlanId) {
          // Add selling plan allocation and clear the manual-payment properties.
          // A card-payment add carries no _sub_engine_selling_plan, so drop it (null deletes
          // the key) or a later add of the same product lands on a separate cart line.
          await updateCartItem(item.key, {
            sellingPlanId: sellingPlanId,
            properties: { _sellingplan: "", _sub_engine_selling_plan: null },
          });
        }
      }
    }
  }
}

/**
 * Update cart item (properties and/or selling plan)
 */
async function updateCartItem(itemKey, options = {}) {
  try {
    const currentItem = cartDataForPaymentSelectionInCart.items.find(item => item.key === itemKey);
    if (!currentItem) {
      console.log('Item not found for update:', itemKey);
      return;
    }
    
    const updateData = {
      id: itemKey,
      quantity: currentItem.quantity
    };
    
    // Handle properties update
    if (options.properties) {
      const updatedProperties = {
        ...currentItem.properties
      };
      
      // Process each property - remove if value is null, otherwise add/update
      Object.entries(options.properties).forEach(([key, value]) => {
        if (value === null) {
          delete updatedProperties[key];
        } else {
          updatedProperties[key] = value;
        }
      });
      
      updateData.properties = updatedProperties;
    }
    
    // Handle selling plan update
    if (options.sellingPlanId !== undefined) {
      updateData.selling_plan = options.sellingPlanId || null;
    }
    
    const response = window.JPCartService
      ? await window.JPCartService.enqueuePost('/cart/change.js', JSON.stringify(updateData), { 'Content-Type': 'application/json', 'Accept': 'application/json' })
      : await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
    
    if (response.ok) {
      const updatedCart = await response.json();
      cartDataForPaymentSelectionInCart = updatedCart;
      
    } else {
      console.error('Failed to update item:', itemKey, response.statusText);
    }
  } catch (error) {
    console.error('Error updating item:', itemKey, error);
  }
}

/**
 * Ensure a valid payment method is selected after hiding options
 */
async function ensureValidPaymentMethodSelected(paymentMethodValue) {
  const selectedMethod = cartPaymentOptionWrapper?.querySelector('input[name="payment-method"]:checked');
  
  // If selected method is now hidden/disabled, select the first available one
  if (
    (selectedMethod &&
      (selectedMethod.disabled ||
        selectedMethod
          .closest(".payment-method__option")
          .classList.contains("display-none"))) ||
    (cartPaymentOptionWrapper && !selectedMethod)
  ) {
    const firstAvailable = [
      ...cartPaymentOptionWrapper.querySelectorAll(
        'input[name="payment-method"]:not([disabled])'
      ),
    ].find((input) => !input.closest('.payment-method__option').classList.contains('display-none'));
    if (firstAvailable) {
      firstAvailable.checked = true;
    }
  }
  // Update cart attribute when auto-selecting
  await updateCartPaymentMethodAttribute(paymentMethodValue);
}

/**
 * Update payment method visibility based on cart purchase type
 */
async function updatePaymentMethods() {
  const showPaymentInCart = KROWN.settings.payment_method_selection.should_show_in_cart;
  const purchaseType = getCartPurchaseType();

  if (cartPaymentOptionWrapper) filterAndShowPaymentMethods(purchaseType);

  // Handle empty cart - hide entire payment method container
  if (!cartDataForPaymentSelectionInCart || cartDataForPaymentSelectionInCart.items.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "none";
    }
    showCheckoutButtons()
    
    return;
  }
  // Handle no payment method configured - hide entire payment method container
  if (cartAvailablePaymentMethods.one_time.length === 0 && cartAvailablePaymentMethods.subscription.length === 0 && cartAvailablePaymentMethods.mixed.length === 0  ) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();

    return;
  }
  // Handle no payment method configured for onetime - hide entire payment method container
  if (purchaseType === "onetime" && cartAvailablePaymentMethods.one_time.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();
    return;
  }

  // Handle no payment method configured for subscription - hide entire payment method container  
  if (purchaseType === "subscription" && cartAvailablePaymentMethods.subscription.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();
    return;
  }
  // Handle no payment method configured for mixed - hide entire payment method container
  if (purchaseType === "mixed" && cartAvailablePaymentMethods.mixed.length === 0) {
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
    hideCheckoutButtons();
    return;
  }

  // hide the no payment methods message if it is shown
  hideNoPaymentMethodsMessage();

  //  Hide payment selection if onetime + only Credit/Debit Card available
  if (purchaseType === "onetime") {
    // Hide the payment method container if it exists
    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "none";
    }

    const oneTimeMethods = cartAvailablePaymentMethods.one_time || [];
    const hasSingleMethod = oneTimeMethods.length === 1;
    const selectedMethodOntime = hasSingleMethod ? oneTimeMethods[0] : undefined;

    // Ensure a valid payment method is selected
    await ensureValidPaymentMethodSelected(selectedMethodOntime);

    // Update cart attribute based on payment methods count
    await updateCartAttribute(!hasSingleMethod, oneTimeMethods);

    // Show checkout buttons
    showCheckoutButtons();

    return;
  }

  // Condition 2: Hide payment selection if subscription/mixed + only Credit/Debit Card available
  if (
      (purchaseType === "subscription" && cartAvailablePaymentMethods.subscription.length === 1) ||
      (purchaseType === "mixed" && cartAvailablePaymentMethods.mixed.length === 1)
  ) {

    await updateCartAttribute(
      false,
      purchaseType === "subscription"
      ? cartAvailablePaymentMethods.subscription
      : cartAvailablePaymentMethods.mixed
    );

    const singleMethodPaymentType =
      purchaseType === "subscription"
        ? cartAvailablePaymentMethods.subscription[0]
        : cartAvailablePaymentMethods.mixed[0];

      // ensureValidPaymentMethodSelected populates cartSelectedPaymentMethod
      // via updateCartPaymentMethodAttribute. Must run BEFORE we decide whether
      // to show the checkout button — single-market single-method stores have
      // cartSelectedPaymentMethod === null at this point on first load, so
      // gating showCheckoutButtons on it earlier kept the buttons hidden.
      await ensureValidPaymentMethodSelected(singleMethodPaymentType);
      if (cartSelectedPaymentMethod) showCheckoutButtons();

      if (cartPaymentOptionWrapper) {
        cartPaymentOptionWrapper.style.display = "none";
      }else{
        await handleSellingPlanAllocations(singleMethodPaymentType);
      }

      return;
  }

  // Handle subscription/mixed payments for Mixed market
  if (
    (purchaseType === "subscription" || purchaseType === "mixed") &&
    showPaymentInCart
  ) {
    const purchaseTypePaymentList = cartAvailablePaymentMethods[purchaseType] || [];
    const existingPaymentAttributeValue = cartDataForPaymentSelectionInCart.attributes?._paymentMethod;
    let fallbackMethod;

    if (
      existingPaymentAttributeValue &&
      purchaseTypePaymentList.includes(existingPaymentAttributeValue)
    ) {
      fallbackMethod = existingPaymentAttributeValue;
    } else {
      const onlineIndex = purchaseTypePaymentList.findIndex(
        (method) => method === "Online"
      );

      fallbackMethod =
        onlineIndex !== -1
          ? purchaseTypePaymentList[onlineIndex]
          : purchaseTypePaymentList[0];
    }

    // Ensure a valid payment method is selected
    await ensureValidPaymentMethodSelected(
      cartPaymentOptionWrapper ? undefined : fallbackMethod
    );
    
    // Update cart attribute _newFlow to false
    await updateCartAttribute(false, purchaseTypePaymentList);

    if (cartPaymentOptionWrapper) {
      cartPaymentOptionWrapper.style.display = "block";
      showCheckoutButtons();
    } else {
      hideCheckoutButtons();
    }
    return;
  }

  // Hide checkout button and show no payment methods message if subscription/mixed have mixed payment and not configured to show in cart
  if (
    ((purchaseType === "subscription" && cartAvailablePaymentMethods.subscription.length > 1) || (purchaseType === "mixed" && cartAvailablePaymentMethods.mixed.length > 1)) &&
    !showPaymentInCart
  ) {    
    if (cartPaymentOptionWrapper) {
      const allPaymentMethods = cartPaymentOptionWrapper.querySelectorAll(".payment-method__option");
      allPaymentMethods.forEach(method => {
        method.classList.add("display-none");
      });
      cartPaymentOptionWrapper.style.display = "block";
      showNoPaymentMethodsMessage();
    }
     hideCheckoutButtons();
     return;
  }
}

/**
 * Setup listeners for cart updates and payment method changes
 */
function setupCartListeners() {
  // Listen for payment method changes
  if (cartPaymentOptionWrapper) {
    const paymentMethodInputs = cartPaymentOptionWrapper.querySelectorAll('input[name="payment-method"]');
    paymentMethodInputs.forEach(input => {
      input.addEventListener('change', async (e) => {
        const cartForm = document.querySelector("form#cart.cart__form");
        // Add the "processing" class
        document.getElementById("page-loader").classList.remove("hidden");
        if (cartForm) {
          cartForm.classList.add("processing");
        }
        hideCheckoutButtons();
        try {
          await updateCartPaymentMethodAttribute();
        } catch (error) {
          console.error('updateCartPaymentMethodAttribute failed', error);
        } finally {
          // Always restore checkout buttons, processing state and hide the loader —
          // even if the update threw — so the overlay never gets stuck.
          showCheckoutButtons();
          if (cartForm) {
            cartForm.classList.remove("processing");
          }
          document.getElementById("page-loader")?.classList.add("hidden");
        }
      });
    });
  }
}

/**
 * Initialize cart payment methods controller
 */
async function initCartPaymentMethods() {
  try {
    // Get payment method container
    cartPaymentOptionWrapper = document.querySelector('.payment-method-cart');
    if(cartPaymentOptionWrapper){
      document.getElementById("page-loader").classList.remove("hidden");
    }

    cartAvailablePaymentMethodsFromSettings();

    // Initial setup
    await loadcartDataForPaymentSelectionInCart()

    if(cartPaymentOptionWrapper){
        // Set initial payment method selection based on cart attribute
        const currentPaymentMethod = cartDataForPaymentSelectionInCart?.attributes?._paymentMethod;
        if (currentPaymentMethod) {
          const paymentMethodInput = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${currentPaymentMethod}"]`);
          if (paymentMethodInput) {
            paymentMethodInput.checked = true;
          }
        }
    }

    await updatePaymentMethods();


    // Setup listeners
    setupCartListeners();
  } catch (error) {
    // Surface the real failure (helps diagnose locale-specific init issues, e.g. ES/FR/DE/PL)
    // instead of swallowing it silently.
    console.error('initCartPaymentMethods failed', error);
  } finally {
    // Always hide the loader if we showed it — even if init threw above — so the
    // page-loader overlay can never persist indefinitely.
    if (cartPaymentOptionWrapper) {
      document.getElementById("page-loader")?.classList.add("hidden");
    }
    window.cartPaymentMethodsInitialized = true;
    document.dispatchEvent(new CustomEvent('cartPaymentMethodsReady'));
  }
  document.dispatchEvent(new CustomEvent('cartPaymentMethodsReady'));
}

/**
 * Public method to manually refresh payment methods
 */
// options.force === false lets a caller that has just completed a cart write skip the
// re-read: cart-service.js seeds its cache from every update/change response, so the
// cached cart is already the authoritative post-mutation one. Defaults to forcing, so
// existing callers keep re-reading.
async function refreshCartPaymentMethods(options) {
  ensurePaymentSettings();
  await loadcartDataForPaymentSelectionInCart(options?.force !== false);
  await updatePaymentMethods();
}

function filterAndShowPaymentMethods(purchaseType) {
  const allPaymentMethods = cartPaymentOptionWrapper.querySelectorAll(".payment-method__option");
  allPaymentMethods.forEach(method => {
    method.classList.add("display-none");
  });

  if(purchaseType === "onetime"){
    cartAvailablePaymentMethods.one_time.forEach(paymentMethod => {
      const paymentMethodElement = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${paymentMethod}"]`);
      if(paymentMethodElement){
        const labelElement = paymentMethodElement.closest('.payment-method__option'); // Get the label
        if(labelElement){
          labelElement.classList.remove("display-none"); // This targets the label
        }
      }
    });
  }else if(purchaseType === "subscription"){
    cartAvailablePaymentMethods.subscription.forEach(paymentMethod => {
      const paymentMethodElement = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${paymentMethod}"]`);
      if(paymentMethodElement){
        const labelElement = paymentMethodElement.closest('.payment-method__option'); // Get the label
        if(labelElement){
          labelElement.classList.remove("display-none"); // This targets the label
        }
      }
    }); 
    
  }else if(purchaseType === "mixed"){
    cartAvailablePaymentMethods.mixed.forEach(paymentMethod => {
      const paymentMethodElement = cartPaymentOptionWrapper.querySelector(`input[name="payment-method"][value="${paymentMethod}"]`);
      if(paymentMethodElement){
        const labelElement = paymentMethodElement.closest('.payment-method__option'); // Get the label
        if(labelElement){
          labelElement.classList.remove("display-none"); // This targets the label
        }
      }
    });
  }

}

function cartAvailablePaymentMethodsFromSettings () {
  const {
    gocardless_countries_one_time,
    gocardless_countries_subscription,
    gocardless_countries_mixed,
    online_countries_one_time,
    online_countries_subscription,
    online_countries_mixed,
    klarna_countries_one_time,
    klarna_countries_subscription,
    klarna_countries_mixed,
    banktransfer_countries_one_time,
    banktransfer_countries_subscription,
    banktransfer_countries_mixed,
    bancontact_countries_one_time,
    bancontact_countries_subscription,
    bancontact_countries_mixed,
    ideal_countries_one_time,
    ideal_countries_subscription,
    ideal_countries_mixed,
    tranzila_countries_one_time,
    tranzila_countries_subscription,
    tranzila_countries_mixed,
    cashcheque_countries_one_time,
    cashcheque_countries_subscription,
    cashcheque_countries_mixed,
    blik_countries_one_time,
    blik_countries_subscription,
    blik_countries_mixed,
  } = KROWN.settings.available_payment_methods;
  const country = KROWN.settings.current_country_code;


  // Helper: check if a setting includes the current country
  function includesCountry(setting) {
    if (!setting) return false;
    return setting.split(",").map(c => c.trim()).includes(country);
  }

  const paymentMethods = {
    one_time: [],
    subscription: [],
    mixed: [],
  };

  // One-time
  if (includesCountry(gocardless_countries_one_time)) {
    paymentMethods.one_time.push("gocardless");
  }
  if (includesCountry(online_countries_one_time)) {
    paymentMethods.one_time.push("Online");
  }
  if (includesCountry(klarna_countries_one_time)) {
    paymentMethods.one_time.push("klarna");
  }
  if (includesCountry(banktransfer_countries_one_time)) {
    paymentMethods.one_time.push("banktransfer");
  }
  if (includesCountry(bancontact_countries_one_time)) {
    paymentMethods.one_time.push("bancontact");
  }
  if (includesCountry(ideal_countries_one_time)) {
    paymentMethods.one_time.push("ideal");
  }
  if (includesCountry(tranzila_countries_one_time)) {
    paymentMethods.one_time.push("tranzila");
  }
  if (includesCountry(cashcheque_countries_one_time)) {
    paymentMethods.one_time.push("cashcheque");
  }
  if (includesCountry(blik_countries_one_time)) {
    paymentMethods.one_time.push("blik");
  }

  // Subscription
  if (includesCountry(gocardless_countries_subscription)) {
    paymentMethods.subscription.push("gocardless");
  }
  if (includesCountry(online_countries_subscription)) {
    paymentMethods.subscription.push("Online");
  }
  if (includesCountry(klarna_countries_subscription)) {
    paymentMethods.subscription.push("klarna");
  } 
  if (includesCountry(banktransfer_countries_subscription)) {
    paymentMethods.subscription.push("banktransfer");
  }
  if (includesCountry(bancontact_countries_subscription)) {
    paymentMethods.subscription.push("bancontact");
  }
  if (includesCountry(ideal_countries_subscription)) {
    paymentMethods.subscription.push("ideal");
  }
  if (includesCountry(tranzila_countries_subscription)) {
    paymentMethods.subscription.push("tranzila");
  }
  if (includesCountry(cashcheque_countries_subscription)) {
    paymentMethods.subscription.push("cashcheque");
  }
  if (includesCountry(blik_countries_subscription)) {
    paymentMethods.subscription.push("blik");
  }

  // Mixed
  if (includesCountry(gocardless_countries_mixed)) {
    paymentMethods.mixed.push("gocardless");
  }
  if (includesCountry(online_countries_mixed)) {
    paymentMethods.mixed.push("Online");
  }
  if (includesCountry(klarna_countries_mixed)) {
    paymentMethods.mixed.push("klarna");
  } 
  if (includesCountry(banktransfer_countries_mixed)) {
    paymentMethods.mixed.push("banktransfer");
  }
  if (includesCountry(bancontact_countries_mixed)) {
    paymentMethods.mixed.push("bancontact");
  }
  if (includesCountry(ideal_countries_mixed)) {
    paymentMethods.mixed.push("ideal");
  }
  if (includesCountry(tranzila_countries_mixed)) {
    paymentMethods.mixed.push("tranzila");
  }
  if (includesCountry(cashcheque_countries_mixed)) {
    paymentMethods.mixed.push("cashcheque");
  }
  if (includesCountry(blik_countries_mixed)) {
    paymentMethods.mixed.push("blik");
  }

  cartAvailablePaymentMethods = paymentMethods;
  window.cartAvailablePaymentMethods = cartAvailablePaymentMethods;

}

// Function to hide checkout buttons from mini cart
function hideCheckoutButtons() {
  const customCheckoutButton = document.getElementById(
    "custom-checkout-button"
  );
  const guestCheckoutButton = document.getElementById("CheckOut");
  const loader = document.querySelector(".cart-buttons-loader");
  const buttonsContainer = document.querySelector(".flex-buttons.mini-cart-buttons-container");

  if (customCheckoutButton) {
    customCheckoutButton.classList.add("display-none");
  }

  if (guestCheckoutButton) {
    guestCheckoutButton.classList.add("display-none");
  }

  if (loader) loader.style.display = "none";
  if (buttonsContainer) buttonsContainer.style.display = "";
  const sectionEl = document.querySelector("[data-checkout-buttons-section]");
  if (sectionEl && typeof window.hideCheckoutButtoninCheckoutSection === "function") {
    console.log('hideCheckoutButtoninCheckoutSection called');
    window.hideCheckoutButtoninCheckoutSection();
  }
}

// Function to show checkout buttons from mini cart
function showCheckoutButtons() {
    const customLoader = document.getElementById("custom-button-loader");
    const customText = document.getElementById("custom-button-text");
    const guestLoader = document.getElementById("guest-button-loader");
    const guestText = document.querySelector(".guest-user-button-text");
    const loader = document.querySelector(".cart-buttons-loader");
    const buttonsContainer = document.querySelector(".flex-buttons.mini-cart-buttons-container");

    const customCheckoutButton = document.getElementById(
      "custom-checkout-button"
    );
    const guestCheckoutButton = document.getElementById("CheckOut");

    if (customLoader && customText) {
      customLoader.classList.add("display-none");
      customText.classList.remove("display-none");
      customCheckoutButton.disabled = false;
      // Symmetric counterpart to hideCheckoutButtons adding display-none to
      // the button — otherwise once hidden (e.g. during empty-cart init
      // before shared-cart restore commits items), the button stays hidden
      // on first load even after we flip the loader/text.
      customCheckoutButton.classList.remove("display-none");
    }

    if (guestLoader && guestText) {
      guestLoader.classList.add("display-none");
      guestText.classList.remove("display-none");
      guestCheckoutButton.disabled = false;
      guestCheckoutButton.classList.remove("display-none");
    }
    if (loader) loader.style.display = "none";
    if (buttonsContainer) buttonsContainer.style.display = "";
    if (document.querySelector("[data-checkout-buttons-section]") && typeof window.showCheckoutButtoninCheckoutSection === "function") {
      console.log('showCheckoutButtoninCheckoutSection called');
      window.showCheckoutButtoninCheckoutSection();
    }
}

function showNoPaymentMethodsMessage() {
  const message = cartPaymentOptionWrapper?.querySelector(
    ".payment-method-message"
  );
  if (message) {
    message.style.display = "block";
  }
}

function hideNoPaymentMethodsMessage() {
  const message = cartPaymentOptionWrapper?.querySelector(
    ".payment-method-message"
  );
  if (message) {
    message.style.display = "none";
  }
}

let cartPaymentMethodsInitialized = false;
function safeInitCartPaymentMethods() {
  if (cartPaymentMethodsInitialized) return;
  cartPaymentMethodsInitialized = true;
  initCartPaymentMethods();
}

if (window.location.pathname.includes("/cart")) {
  document.addEventListener("customCartLoaded", () => {
    safeInitCartPaymentMethods();
  });
} else {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      safeInitCartPaymentMethods();
    });
  } else {
    safeInitCartPaymentMethods();
  }
}

// Export functions for global access
window.refreshCartPaymentMethods = refreshCartPaymentMethods;
window.JPCartPaymentMethods = {
  rules: () => ({
    available: {
      one_time: [...availableMethodsFor("one_time")],
      subscription: [...availableMethodsFor("subscription")],
      mixed: [...availableMethodsFor("mixed")],
    },
  }),
  resolveMethod: resolveCartPaymentMethod,
};
window.getCartPurchaseType = getCartPurchaseType;
window.updatePaymentMethods = updatePaymentMethods;
